import { useDispatch } from 'react-redux'
import { useCallback } from 'react'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import {
  AutoTailoringFromPublicJobBoardStep,
  selectors,
  actions,
  ResumeSectionId,
  AddCustomSectionPayload,
  MoveSectionPayload,
  SectionNames,
  DownloadDocumentHandler,
  ResumeEditorStore,
} from 'builder/modules/resumeEditor'
import { selectors as generalEditorSelectors } from 'builder/modules/generalEditor'
import PersonalDetailsSection from 'builder/components/PersonalDetailsSection'
import SectionsList from 'builder/components/SectionsList'
import Anchor from 'builder/components/Anchor'
import {
  CardOperationFunc,
  MoveCardFunc,
  MoveSubItemFunc,
  UpdateCardFunc,
} from 'builder/components/Card'
import { DeleteSectionFunc, RenameSectionFunc } from 'builder/components/Section'
import { ATS_TEMPLATES } from 'builder/components/Helper/constants'
import { TunerEditorProvider } from 'builder/components/Tuner'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { DownloadButton, EditorMobileFooter } from 'builder/components/PreviewPanel'
import { FORMATS } from 'builder/modules/constants'
import { useStepByStepFeature } from '../../StepByStepFlow/hooks/useStepByStepFeature'
import { TabsResumeScore } from '../../TabsResumeScore'
import ATSPersonalDetailsSection from '../../ATSResumeEditor/PersonalDetailsSection'
import { AiWriterButton } from '../../AiWriter/AiWriterButton'
import { FinnishTailoringButton, StartTailoringButton } from '../../TailoringFromPublicJobBoard'
import { Container, StepByStepContainer, StyledStepByStepFlow } from './styles'

type Props = {
  resumeState: ResumeEditorStore
  canScrollBottom?: boolean
  showResumeDistributionModal?: (format: FORMATS) => void
  handleDownloadDocument: DownloadDocumentHandler
  handleShareDocument?: () => void
}

export const EditContent = (props: Props) => {
  const {
    resumeState,
    canScrollBottom,
    showResumeDistributionModal,
    handleDownloadDocument,
    handleShareDocument,
  } = props
  const { isRbpStepFlowActive } = useStepByStepFeature()
  const dispatch = useDispatch()
  const autoTailoringFromPublicJobBoard = useTypedSelector(
    selectors.autoTailoringFromPublicJobBoard,
  )
  const levels = useTypedSelector(generalEditorSelectors.levels)
  const resume = useTypedSelector(selectors.resume)
  const { isAboveTablet } = useMediaQueries()

  const showScoreContainer =
    !autoTailoringFromPublicJobBoard.enabled ||
    autoTailoringFromPublicJobBoard.step === AutoTailoringFromPublicJobBoardStep.finished

  const handleSimpleFieldUpdate = useCallback(
    (name: any, value: any, debounce?: boolean) => {
      dispatch(actions.updateSimpleField({ name, value, debounce }))
    },
    [dispatch],
  )

  const updateCard: UpdateCardFunc = (sectionId, cardId, values, debounce, isCustom) =>
    dispatch(actions.updateCard({ sectionId, cardId, values, debounce, isCustom }))

  const addCard = (
    cardId: string,
    sectionName: ResumeSectionId,
    options?: { shouldOpen: boolean },
  ) => dispatch(actions.addCard({ sectionName, cardId, options, debounce: true }))

  const deleteCard: CardOperationFunc = (cardId, sectionName, isCustom = false) =>
    dispatch(actions.deleteCard({ sectionName, cardId, isCustom }))

  const addCustomSection = (externalId: string, options: AddCustomSectionPayload['options']) =>
    dispatch(actions.addCustomSection({ externalId, options, debounce: true }))

  const moveCard: MoveCardFunc = payload => dispatch(actions.moveCard(payload))

  const moveSection = (payload: MoveSectionPayload) => dispatch(actions.moveSection(payload))

  const deleteSection: DeleteSectionFunc = (id, options) =>
    dispatch(actions.deleteSection({ id, options: { ...options } }))

  const renameSection: RenameSectionFunc = payload =>
    dispatch(actions.renameSection({ ...payload, debounce: true }))

  const addCardSubItem: CardOperationFunc = (cardId, sectionName) =>
    dispatch(actions.addCardSubItem({ sectionName, cardId }))

  const moveCardSubItem: MoveSubItemFunc = payload => dispatch(actions.moveCardSubItem(payload))

  const isATSTemplate = !!resume && ATS_TEMPLATES.includes(resume.template)

  if (!resume) {
    return null
  }

  return isRbpStepFlowActive ? (
    <StepByStepContainer>
      <StyledStepByStepFlow
        resume={resume}
        updateCard={updateCard}
        addCard={addCard}
        addCustomSection={addCustomSection}
        deleteCard={deleteCard}
        moveCard={moveCard}
        moveSection={moveSection}
        levels={levels}
        updateSimpleField={handleSimpleFieldUpdate}
        renameSection={renameSection}
        deleteSection={deleteSection}
        onAddCardSubItem={addCardSubItem}
        onSubItemMove={moveCardSubItem}
        isHeaderFullBorderRadius={true}
      />
    </StepByStepContainer>
  ) : (
    <TunerEditorProvider resume={resume}>
      <Container>
        {showScoreContainer && <TabsResumeScore />}
        <Anchor id={SectionNames.details} scrollOffsetTop={70}>
          {isATSTemplate ? (
            <ATSPersonalDetailsSection resume={resume} />
          ) : (
            <PersonalDetailsSection resume={resume} />
          )}
        </Anchor>
        <SectionsList
          resume={resume}
          updateCard={updateCard}
          addCard={addCard}
          addCustomSection={addCustomSection}
          deleteCard={deleteCard}
          moveCard={moveCard}
          moveSection={moveSection}
          levels={levels}
          updateSimpleField={handleSimpleFieldUpdate}
          renameSection={renameSection}
          deleteSection={deleteSection}
          addCardSubItem={addCardSubItem}
          moveCardSubItem={moveCardSubItem}
        />
        {!isAboveTablet && (
          <EditorMobileFooter isFooterShadowVisible={canScrollBottom}>
            {resumeState.editorPanelType === 'editor' && <AiWriterButton />}
            {autoTailoringFromPublicJobBoard.enabled ? (
              <>
                {autoTailoringFromPublicJobBoard.step ===
                  AutoTailoringFromPublicJobBoardStep.step1 && <StartTailoringButton />}

                {autoTailoringFromPublicJobBoard.step ===
                  AutoTailoringFromPublicJobBoardStep.finished && <FinnishTailoringButton />}
              </>
            ) : (
              <DownloadButton
                showResumeDistributionModal={showResumeDistributionModal}
                handleDownloadDocument={handleDownloadDocument}
                handleShareDocument={handleShareDocument}
                shouldShowIcon={false}
                menuPosition="top"
              />
            )}
          </EditorMobileFooter>
        )}
      </Container>
      {isAboveTablet && resumeState.editorPanelType === 'editor' && <AiWriterButton />}
    </TunerEditorProvider>
  )
}
